/**
 * The Ask Focal chat surface, as class strings.
 *
 * Two chats wear it: Ask Focal on a conversation/client/meeting prep (which
 * runs on assistant-ui, in `components/assistant-ui/thread.tsx`) and Ask Focal
 * on a deliverable (which runs on its own SSE controller, in
 * `DeliverableChat.tsx`). They share no runtime and never will — the whole
 * point of this module is that they should still be indistinguishable to look
 * at, and that a change to the surface lands in both at once rather than in
 * whichever one the next person happened to open.
 *
 * Only what is genuinely shared lives here. Anything one chat has and the
 * other does not — the deliverable's proposal card and `note` alerts, the
 * conversation's branch picker — stays with the chat that owns it.
 */

import type { CSSProperties } from "react";

/** The panel the chat sits in: its own tint, divided from the page by its edge. */
export const CHAT_PANEL_SURFACE = "bg-[#fafcff] border-l border-border-light";

/** Sparkle and wordmark on the left, close on the right. */
export const CHAT_PANEL_HEADER =
  "flex h-14 shrink-0 items-center justify-between gap-2 px-4 py-3 bg-white";

/**
 * The bare line the panel is resized by. A 1px rule with a 4px invisible grab
 * area over it, so the pointer target is usable without a visible bar.
 */
export const CHAT_RESIZE_HANDLE =
  "absolute left-0 top-0 bottom-0 w-px bg-border-light cursor-ew-resize z-10 after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:outline-2 focus-visible:outline-blue-700 focus-visible:outline-offset-2";

/**
 * The transcript's scroll container. `border-t` is always present and
 * transparent until the reader scrolls — see `CHAT_VIEWPORT_BORDER` — so the
 * rule appearing under the header never shifts the content by a pixel.
 */
export const CHAT_VIEWPORT =
  "relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto scroll-smooth border-t [&]:scrollbar-thin [&]:scrollbar-track-transparent [&]:scrollbar-thumb-border [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border transition-colors";

/** Applied to `CHAT_VIEWPORT` on scroll position, not on scroll height. */
export const CHAT_VIEWPORT_BORDER = (isScrolled: boolean): string =>
  isScrolled ? "border-border-light" : "border-transparent";

/** Wraps the whole transcript. `mt-auto` keeps a short one pinned to the composer. */
export const CHAT_TRANSCRIPT = (isWorking: boolean): string =>
  `mt-auto w-full px-4 pt-4 ${isWorking ? "pb-0" : "pb-8"}`;

/** Between turns. Wide on purpose: it is what separates one exchange from the next. */
export const CHAT_MESSAGE_GROUP = "flex flex-col gap-y-8 pb-4";

/** Every message arrives the same way. */
export const CHAT_MESSAGE_ENTER =
  "fade-in slide-in-from-bottom-1 animate-in duration-motion-fast ease-emphasized";

/**
 * The assistant's bubble, sized to its content and left-aligned. Combine with
 * exactly one of the two states below.
 */
export const CHAT_ASSISTANT_BUBBLE =
  "wrap-break-word inline-block w-fit max-w-full rounded-xl bg-[#fafcff] p-3 text-sm leading-5 transition-colors duration-motion-default ease-emphasized";

/** While the answer is still arriving: no border, dimmed, and pulsing. */
export const CHAT_ASSISTANT_BUBBLE_STREAMING =
  "p-2 text-typography-disabled [&_a]:text-typography-disabled";

/** Once the turn has settled. */
export const CHAT_ASSISTANT_BUBBLE_SETTLED =
  "border border-border-light text-typography-primary";

/** The pulse that runs on a streaming bubble. Keyframes live in `globals.css`. */
export const CHAT_ASSISTANT_BUBBLE_PULSE: CSSProperties = {
  animation: "aui-bubble-pulse 1.6s ease-in-out infinite",
};

// Reserve hover controls without adding their height to the gap between turns.
export const CHAT_ASSISTANT_ACTION_BAR_SPACE = "-mb-7.5 min-h-7.5 pt-1.5";

/**
 * The user's turn. A two-column grid rather than `items-end` so the hover
 * actions get a reserved gutter on the left instead of overlapping the bubble,
 * and so a short message can never stretch past `100% - 72px`.
 */
export const CHAT_USER_MESSAGE_ROOT =
  "grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 [&:where(>*)]:col-start-2";

export const CHAT_USER_BUBBLE =
  "wrap-break-word peer rounded-xl bg-[#fafafa] p-3 text-sm leading-5 text-typography-primary empty:hidden";

/** An opener on the welcome screen, and the `what else can I ask` card under them. */
export const CHAT_SUGGESTION_CARD =
  "w-full flex items-start gap-2 rounded-xl border border-border-light bg-[#fafcff] p-3 text-left transition-colors hover:bg-[var(--color-secondary-hover-op)]";

/**
 * The greeting. Sized off the panel, not the viewport — the chat is resizable,
 * so `@container` is the only thing that knows how much room it actually has.
 * Requires `@container` on an ancestor.
 */
export const CHAT_GREETING =
  "bg-clip-text text-transparent text-xl font-semibold text-center leading-tight tracking-tight break-words @[360px]:text-2xl @[360px]:leading-8";

export const CHAT_GREETING_GRADIENT: CSSProperties = {
  backgroundImage:
    "linear-gradient(90deg, #2563eb 10%, #3b82f6 40%, #5e71f6 63%, #8b5cf6 92%)",
};

/** Everything below the transcript: context pills, composer, disclaimer. */
export const CHAT_COMPOSER_SECTION =
  "bg-background-tone border-t border-blue-100 px-4 pb-10 pt-4";

/** The 1.5px gradient edge that is the composer's focus state. */
export const CHAT_COMPOSER_EDGE = "rounded-xl p-[1.5px] transition-colors";

/** Lit while there is something to send. */
export const CHAT_COMPOSER_EDGE_ACTIVE =
  "bg-[linear-gradient(135deg,#2563EB_0%,#9333EA_100%)]";

/** Otherwise plain, and half-lit on focus. */
export const CHAT_COMPOSER_EDGE_IDLE =
  "bg-border focus-within:bg-[linear-gradient(135deg,rgba(37,99,235,0.5)_0%,rgba(147,51,234,0.5)_100%)]";

export const CHAT_COMPOSER_SHELL =
  "flex w-full flex-col gap-2 rounded-[10.5px] bg-background p-2";

export const CHAT_COMPOSER_INPUT =
  "max-h-32 min-h-9 w-full resize-none bg-transparent px-1 py-1 text-sm outline-none placeholder:text-typography-secondary disabled:cursor-not-allowed disabled:placeholder:text-typography-disabled";

export const CHAT_COMPOSER_SEND = "size-8 rounded-md border-typography-disabled";

/**
 * Constrained and inset from the right so it cannot sit under the fixed
 * bottom-right Pylon bubble (FE-1166).
 */
export const CHAT_DISCLAIMER = "pe-16 text-center";

/** Floats over the top of the composer section. */
export const CHAT_SCROLL_TO_BOTTOM =
  "absolute -top-12 left-1/2 -translate-x-1/2 z-10 rounded-full p-4 disabled:invisible dark:border-border dark:bg-background dark:hover:bg-accent";
